/** Contract Detail page — mirrors the Job Detail layout.
 *
 * Left column: Summary + per-agent tabs reading agent_results directly.
 * Right column (sticky on lg): source PDF + Ask contract panel.
 *
 * The action row in the header is the single place a reviewer can:
 *   * open the underlying pipeline job
 *   * escalate the contract to another user (final Gate stage)
 *   * edit metadata
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { AlertCircle, ArrowLeft, ClipboardCheck, ExternalLink, MessageSquare, Pencil, RefreshCw, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';
import { api, ApiError } from '@/lib/api';
import { ContractHeader } from './ContractHeader';
import { ContractMetadataCard } from './ContractMetadataCard';
import { ContractStageTabs, type ContractStageTabKey } from './ContractStageTabs';
import { SourcePdfCard } from '@/pages/jobs/SourcePdfCard';
import { AskContractPanel } from '@/pages/jobs/AskContractPanel';
import { ArchiveContractButton } from './ArchiveContractButton';
import { JobCommentsPanel } from '@/pages/jobs/JobCommentsPanel';
import { EscalateButton } from '@/pages/jobs/stages/EscalateButton';
import { GateReportExportButton } from '@/pages/jobs/GateReportExportButton';
import { FEATURE_ESCALATIONS } from '@/lib/features';
import { fetchReport } from '@/pages/jobs/fetchReport';
import type { ContractDetail } from './types';
import type { AgentResult, Job, GateReport } from '@/types';

export function ContractDetailPage() {
  const { id } = useParams<{ id: string }>();
  const contractId = id ?? '';
  const { canReview, isAdmin } = useAuth();
  const canEdit = canReview;

  const [contract, setContract] = useState<ContractDetail | null>(null);
  const [agents, setAgents] = useState<AgentResult[]>([]);
  const [job, setJob] = useState<Job | null>(null);
  const [report, setReport] = useState<GateReport | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isNotFound, setIsNotFound] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [activityKey, setActivityKey] = useState(0);
  const [stageTab, setStageTab] = useState<ContractStageTabKey>('summary');
  const tabsRef = useRef<HTMLDivElement>(null);
  const askRef = useRef<HTMLDivElement>(null);

  const goToReview = () => {
    setStageTab('gate');
    tabsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  const goToAsk = () => {
    askRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const loadAll = useCallback(async () => {
    if (!contractId) return;
    setIsLoading(true);
    setError(null);
    setIsNotFound(false);
    try {
      const c = await api.get<ContractDetail>(`/api/v1/contracts/${contractId}`);
      setContract(c);
      if (c.job_id) {
        const jobId = c.job_id;
        try {
          const ar = await api.get<AgentResult[]>(`/api/v1/jobs/${jobId}/agents`);
          setAgents(ar);
        } catch {
          setAgents([]);
        }
        // Job + gate report power the Final-gate binding decision. The
        // report only exists once the pipeline synthesises it (completed
        // /failed jobs); fetchReport swallows 202/404 → null.
        try {
          const j = await api.get<Job>(`/api/v1/jobs/${jobId}`);
          setJob(j);
          setReport(
            j.status === 'completed' || j.status === 'failed'
              ? await fetchReport(jobId)
              : null,
          );
        } catch {
          setJob(null);
          setReport(null);
        }
      }
    } catch (err: unknown) {
      if (err instanceof ApiError && err.status === 404) {
        setIsNotFound(true);
      } else {
        setError('Failed to load contract details. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  }, [contractId]);

  useEffect(() => { void loadAll(); }, [loadAll]);

  if (isNotFound) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center gap-4">
        <FileText className="h-12 w-12 text-muted-foreground" aria-hidden="true" />
        <h1 className="text-2xl font-bold">Contract not found</h1>
        <p className="text-sm text-muted-foreground">
          This contract does not exist or you don't have access to it.
        </p>
        <Button variant="outline" asChild>
          <Link to="/contracts"><ArrowLeft className="h-4 w-4 mr-2" aria-hidden="true" />Back to Contracts</Link>
        </Button>
      </div>
    );
  }

  if (error && !isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center gap-4">
        <AlertCircle className="h-12 w-12 text-destructive" aria-hidden="true" />
        <p className="text-sm text-muted-foreground">{error}</p>
        <Button variant="outline" onClick={() => { void loadAll(); }}>
          <RefreshCw className="h-4 w-4 mr-2" aria-hidden="true" />Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-7xl mx-auto">
      <Link
        to="/contracts"
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring rounded"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Contracts
      </Link>

      <ContractHeader
        contract={contract}
        isLoading={isLoading}
        canEdit={canEdit}
        isEditing={isEditing}
        onEditToggle={() => { setIsEditing((e) => !e); }}
      />

      {/* Top action bar — the reviewer's jump-off points for this contract. */}
      <div className="flex items-center gap-2 flex-wrap border-b pb-4">
        {contract?.job_id && (
          <Button size="sm" className="gap-1.5" onClick={goToReview}>
            <ClipboardCheck className="h-4 w-4" aria-hidden="true" />
            Go to review
          </Button>
        )}
        {contract?.job_id && (
          <Button variant="outline" size="sm" asChild className="gap-1.5">
            <Link to={`/jobs/${contract.job_id}`}>
              <ExternalLink className="h-4 w-4" aria-hidden="true" />
              Go to pipeline
            </Link>
          </Button>
        )}
        {contract?.job_id && (
          <Button variant="outline" size="sm" className="gap-1.5" onClick={goToAsk}>
            <MessageSquare className="h-4 w-4" aria-hidden="true" />
            Ask about this contract
          </Button>
        )}
        <span className="mx-1 hidden h-5 w-px bg-border sm:inline-block" aria-hidden="true" />
        {contract?.job_id && (
          <GateReportExportButton jobId={contract.job_id} />
        )}
        {FEATURE_ESCALATIONS && contract?.job_id && (
          <EscalateButton
            jobId={contract.job_id}
            stage="gate"
            agentSummary={contract.contract_summary ?? undefined}
          />
        )}
        {canEdit && (
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5"
            onClick={() => { setIsEditing((e) => !e); }}
          >
            <Pencil className="h-4 w-4" aria-hidden="true" />
            {isEditing ? 'Cancel edit' : 'Edit metadata'}
          </Button>
        )}
        {/* Admin only: removing someone else's in-flight review is not a
            queue-working action. Renders nothing once past review. */}
        {isAdmin && contract && (
          <ArchiveContractButton
            contractId={contract.id}
            contractTitle={contract.title}
            status={contract.status}
          />
        )}
      </div>

      {/* Two-column layout: 5/4 split on lg. Mirrors JobDetailPage. */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-9">
        <div className="lg:col-span-5 space-y-4">
          {contract && (
            <div ref={tabsRef} className="scroll-mt-4">
              <ContractStageTabs
                contract={contract}
                agents={agents}
                job={job}
                report={report}
                active={stageTab}
                onActiveChange={setStageTab}
                onActivity={() => { setActivityKey((n) => n + 1); }}
                onCommentSaved={() => { void loadAll(); }}
                onEditMetadata={() => { setIsEditing(true); }}
              />
            </div>
          )}
          {isEditing && contract && (
            <ContractMetadataCard
              contract={contract}
              isLoading={isLoading}
              isEditing
              onSaved={(updated) => { setContract(updated); setIsEditing(false); }}
              onCancelEdit={() => { setIsEditing(false); }}
            />
          )}
        </div>

        <aside aria-label="Source PDF and chat" className="lg:col-span-4">
          {/* The whole right rail is pinned on large screens and scrolls
              independently of the left column. Height is capped to the
              viewport with internal overflow so the Ask panel and comments
              stay reachable — a full-height sticky PDF used to overlap them
              and swallow pointer events, making the Ask box unusable. */}
          <div className="space-y-4 lg:sticky lg:top-4 lg:max-h-[calc(100vh-2rem)] lg:overflow-y-auto lg:pr-1">
            {contract?.job_id ? (
              <>
                <SourcePdfCard
                  jobId={contract.job_id}
                  onTranslated={() => { setActivityKey((n) => n + 1); }}
                />
                <div ref={askRef} className="scroll-mt-4">
                  <AskContractPanel jobId={contract.job_id} />
                </div>
                <JobCommentsPanel jobId={contract.job_id} refreshKey={activityKey} />
              </>
            ) : (
              <div className="rounded border bg-muted/30 p-4 text-sm text-muted-foreground">
                No pipeline job linked to this contract. Submit it for review to
                see the source PDF and agent analyses here.
              </div>
            )}
            <details className="rounded-lg border bg-card">
              <summary className="cursor-pointer list-none flex items-center justify-between px-4 py-2 text-sm font-medium hover:bg-muted/50 rounded-lg">
                <span>Contract metadata</span>
                <span className="text-xs text-muted-foreground">expand</span>
              </summary>
              <div className="px-2 pb-2 pt-1 border-t">
                <ContractMetadataCard
                  contract={contract}
                  isLoading={isLoading}
                  isEditing={false}
                  onSaved={(updated) => { setContract(updated); }}
                  onCancelEdit={() => { /* no-op */ }}
                />
              </div>
            </details>
          </div>
        </aside>
      </div>
    </div>
  );
}
